import { z } from 'zod';
import {
  IndicatorType,
  VwapAnchorType,
  VwapMode,
  VwapSessionReset
} from '@shared/types';

export const VwapInputSchema = z.object({
  type: z.literal(IndicatorType.VWAP),
  mode: z.nativeEnum(VwapMode).default(VwapMode.SESSION),
  sessionReset: z.nativeEnum(VwapSessionReset).optional(),
  customSessionHourUtc: z.number().int().min(0).max(23).optional(),
  anchorType: z.nativeEnum(VwapAnchorType).optional(),
  anchorTimestamp: z.string().optional(),
  period: z.number().int().positive().optional()
});

export type VwapInput = z.infer<typeof VwapInputSchema>;

const SessionNonCustomSchema = z.object({
  type: z.literal(IndicatorType.VWAP),
  mode: z.literal(VwapMode.SESSION),
  sessionReset: z
    .enum([VwapSessionReset.FOREX_NY_17, VwapSessionReset.UTC_DAY])
    .default(VwapSessionReset.FOREX_NY_17),
  customSessionHourUtc: z.undefined().optional(),
  anchorType: z.undefined().optional(),
  anchorTimestamp: z.undefined().optional(),
  period: z.undefined().optional()
});

const SessionCustomSchema = z.object({
  type: z.literal(IndicatorType.VWAP),
  mode: z.literal(VwapMode.SESSION),
  sessionReset: z.literal(VwapSessionReset.CUSTOM),
  customSessionHourUtc: z.number().int().min(0).max(23),
  anchorType: z.undefined().optional(),
  anchorTimestamp: z.undefined().optional(),
  period: z.undefined().optional()
});

const AnchoredTimestampSchema = z.object({
  type: z.literal(IndicatorType.VWAP),
  mode: z.literal(VwapMode.ANCHORED),
  anchorType: z.literal(VwapAnchorType.TIMESTAMP),
  anchorTimestamp: z.string().min(1),
  sessionReset: z.undefined().optional(),
  customSessionHourUtc: z.undefined().optional(),
  period: z.undefined().optional()
});

const AnchoredPresetSchema = z.object({
  type: z.literal(IndicatorType.VWAP),
  mode: z.literal(VwapMode.ANCHORED),
  anchorType: z
    .enum([VwapAnchorType.WEEK_OPEN, VwapAnchorType.MONTH_OPEN])
    .default(VwapAnchorType.WEEK_OPEN),
  anchorTimestamp: z.undefined().optional(),
  sessionReset: z.undefined().optional(),
  customSessionHourUtc: z.undefined().optional(),
  period: z.undefined().optional()
});

const RollingSchema = z.object({
  type: z.literal(IndicatorType.VWAP),
  mode: z.literal(VwapMode.ROLLING),
  period: z.number().int().positive(),
  sessionReset: z.undefined().optional(),
  customSessionHourUtc: z.undefined().optional(),
  anchorType: z.undefined().optional(),
  anchorTimestamp: z.undefined().optional()
});

const CumulativeSchema = z.object({
  type: z.literal(IndicatorType.VWAP),
  mode: z.literal(VwapMode.CUMULATIVE),
  sessionReset: z.undefined().optional(),
  customSessionHourUtc: z.undefined().optional(),
  anchorType: z.undefined().optional(),
  anchorTimestamp: z.undefined().optional(),
  period: z.undefined().optional()
});

export const StrictVwapInputSchema = z.union([
  SessionNonCustomSchema,
  SessionCustomSchema,
  AnchoredTimestampSchema,
  AnchoredPresetSchema,
  RollingSchema,
  CumulativeSchema
]);

export type StrictVwapInput = z.infer<typeof StrictVwapInputSchema>;
